import * as React from 'react';

import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import PlatformConditional from 'Components/PlatformConditional';
import { FormattedMessage, useIntl } from 'react-intl';
import FormSection from '../../../../../components/FormSection';
import { styled } from '@mui/system';
import colors from '../../../../../config/theme/colors';
import ToolTip from 'Components/ToolTip';
import { useAppSelector } from 'Store/index';
import { arrayOfAll, isNumber } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { Container, Item } from 'Components/Grid';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import { Theme, useMediaQuery } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { path } from 'Utility/ramdaReplacement';
import { alzheimerDiagnoses, TAlzheimerDiagnosis } from 'Routes/Diagnosis/utils/definitions';

const optionFormatter =
  (opt: number | string) =>
  (name: number | string): React.JSX.Element => {
    return <FormattedMessage id={`imaging.${opt}.${name}`} />;
  };

const fmElement = (id: string, values?: { [key: string]: string | number | null }): React.JSX.Element => (
  <FormattedMessage id={id} values={values || undefined} />
);

const StyledSubHeader = styled('div')({
  color: colors.tertiaryText,
  marginBottom: '0.6rem',
});

const StyledSubHeaderCentered = styled(StyledSubHeader)({
  display: 'flex',
  justifyContent: 'center',
  alignContent: 'flex-start',
});

const StyledDiv = styled('div')({
  marginTop: '4rem',
});

const StyledDivCentered = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignContent: 'flex-start',
});

const StyledRowTitle = styled('span')({
  color: colors.tertiaryText,
});

const StyledTable = styled('table')({
  marginLeft: '-0.4rem',
});
const StyledTableBody = styled('tbody')({});
const StyledTableRow = styled('tr', {
  shouldForwardProp: prop => prop !== 'centered',
})(({ centered }: { centered?: boolean }) => ({
  display: 'table-row',
  verticalAlign: centered ? 'center' : 'top',
  '&:first-of-type': {
    verticalAlign: 'top',
  },
}));
const StyledTableColumn = styled('td')({
  display: 'table-cell',
  '&:first-of-type': {
    width: '32%',
  },
  '&:nth-of-type(3)': {
    padding: '0 0 0 1rem',
  },
  '&:nth-of-type(4)': {
    padding: '0 1rem 0 1rem',
  },
});

const StyledPreviousNumerical = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignContent: 'flex-start',
  color: theme.palette.primary.light,
  fontWeight: 600,
}));

const lesionOptions = ['X', 'N/A', 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, '10-20', '21-40', '>40'];

const alzBrainAreas = arrayOfAll.options<TAlzheimerBrainAreas>()([
  'frontalRight',
  'frontalLeft',
  'parietalRight',
  'parietalLeft',
  'occipitalRight',
  'occipitalLeft',
  'temporalRight',
  'temporalLeft',
  'deepGrayMatterStructures',
  'cerebellum',
  'midbrain',
  'pons',
  'medullaOblongata',
  'leptomeninges',
]);

const LesionsFormTitleRow = ({
  title,
  lesionsTitle,
  deltaSymbol,
  diameterSymbol,
  trendTitle,
}: ILesionsFormTitleRow): React.JSX.Element => (
  <StyledTableRow>
    <StyledTableColumn>
      <StyledSubHeader style={{ fontWeight: 600 }}>{fmElement(title)}</StyledSubHeader>
    </StyledTableColumn>
    <StyledTableColumn>
      <StyledSubHeaderCentered>{lesionsTitle && fmElement(lesionsTitle)}</StyledSubHeaderCentered>
    </StyledTableColumn>
    <StyledTableColumn>
      <ToolTip
        description={fmElement('imaging.lesionsDelta')}
        content={<StyledSubHeaderCentered>{deltaSymbol}</StyledSubHeaderCentered>}
        hover={true}
      />
    </StyledTableColumn>
    <StyledTableColumn>
      <ToolTip
        description={fmElement('imaging.lesionsMaxDiameter')}
        content={<StyledSubHeaderCentered>{diameterSymbol}</StyledSubHeaderCentered>}
        hover={true}
      />
    </StyledTableColumn>
    <StyledTableColumn>
      <StyledSubHeaderCentered>{trendTitle && fmElement(trendTitle)}</StyledSubHeaderCentered>
    </StyledTableColumn>
  </StyledTableRow>
);

const LesionsFormDataRow = ({
  formData,
  rowTitle,
  lesions,
  titlePadding,
  prevDoc,
}: ILesionsFormDataRow): React.JSX.Element => {
  const fieldArray = (field: string) => `${lesions}.${field}`.split('.');
  const pathToPrevValue = (field: string) => path(fieldArray(field), prevDoc) as string | number;

  // Track page width and breakpoints
  const mdOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down(1401));

  return (
    <React.Fragment>
      <StyledTableRow centered={!prevDoc && !mdOrSmaller}>
        <StyledTableColumn style={{ paddingRight: titlePadding }}>
          <StyledRowTitle>{fmElement(rowTitle)}</StyledRowTitle>
        </StyledTableColumn>
        <StyledTableColumn>
          <StyledDivCentered>
            <InputHandler
              type="RadioButtonRow"
              editing={true}
              name={`${lesions}.lesions`}
              formData={formData}
              options={lesionOptions}
              tooltips={[
                { index: 0, message: fmElement('imaging.opts.notDoneUnknown.notDone') },
                { index: 1, message: fmElement('imaging.opts.notDoneUnknown.unknown') },
              ]}
              previousValue={pathToPrevValue('lesions')}
            />
          </StyledDivCentered>
        </StyledTableColumn>
        <StyledTableColumn>
          <InputHandler
            type="NumberField"
            editing={true}
            name={`${lesions}.lesionsDelta`}
            formData={formData}
            width={3.5}
            centered
          />
          <StyledPreviousNumerical>{pathToPrevValue('lesionsDelta')}</StyledPreviousNumerical>
        </StyledTableColumn>
        <StyledTableColumn>
          <InputHandler
            type="NumberField"
            editing={true}
            name={`${lesions}.lesionsMaxDiameter`}
            formData={formData}
            width={3.5}
            centered
          />
          <StyledPreviousNumerical>{pathToPrevValue('lesionsMaxDiameter')}</StyledPreviousNumerical>
        </StyledTableColumn>
        <StyledTableColumn>
          <StyledDivCentered>
            <InputHandler
              type="RadioTrendRow"
              editing={true}
              name={`${lesions}.lesionsTrend`}
              formData={formData}
              options={['decreased', 'same', 'increased']}
              previousValue={pathToPrevValue('lesionsTrend')}
            />
          </StyledDivCentered>
        </StyledTableColumn>
      </StyledTableRow>
      <StyledTableRow centered={!prevDoc && !mdOrSmaller}>
        <StyledTableColumn colSpan={5}>
          <div style={{ marginBottom: '1.5rem' }}></div>
        </StyledTableColumn>
      </StyledTableRow>
    </React.Fragment>
  );
};

const MriForm = ({ formData, disabledFields = [] }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });
  const platform = useAppSelector((s: { session: ISessionStore }) => s.session.platforms?.selected);

  const previousMRIDoc: IMRI = useAppSelector(s => {
    const docs = s.documents.sortedAndMergedDocuments;
    const filteredDocument = [...(docs || [])].filter(d => d._type === 'mri')?.[1];
    return filteredDocument;
  });
  const isAlzheimerDiag = useAppSelector(s => {
    const docs = s.documents.sortedAndMergedDocuments;
    const diagDocs = docs?.filter(d => d._type === 'diagnosis');
    return diagDocs?.some((diag: IDiagnosis) =>
      alzheimerDiagnoses.includes((diag.diagnosis ?? '') as TAlzheimerDiagnosis),
    );
  });

  const [previousDocument, setPreviousDocument] = React.useState<IMRI | null>(null);
  const [selectedBrainAreasAriaE, setSelectedBrainAreasAriaE] = React.useState<Array<TAlzheimerBrainAreas>>(
    Object.keys(formData.document.ariaE?.amounts ?? []).filter(area =>
      isNumber(formData.document.ariaE?.amounts?.[area as TAlzheimerBrainAreas]),
    ) as Array<TAlzheimerBrainAreas>,
  );
  const [selectedBrainAreasAriaH, setSelectedBrainAreasAriaH] = React.useState<Array<TAlzheimerBrainAreas>>(
    Object.keys(formData.document.ariaH?.amounts ?? []).filter(area =>
      isNumber(formData.document.ariaH?.amounts?.[area as TAlzheimerBrainAreas]),
    ) as Array<TAlzheimerBrainAreas>,
  );

  const togglePrevious = () => setPreviousDocument(previousDocument ? null : previousMRIDoc || null);

  const [ariaSectionsShown, setAriaSectionsShown] = React.useState<boolean>(
    !!(formData.document.ariaE || formData.document.ariaH || formData.document.ariaNotes),
  );

  const handleChangeBrainAreas =
    (setStateFunction: React.Dispatch<React.SetStateAction<TAlzheimerBrainAreas[]>>) => (values: TOnChangeValues) => {
      const setStateName = Object.keys(values)[0];
      const setStateValue = Object.values(values)[0];

      setStateFunction(setStateValue as TAlzheimerBrainAreas[]);

      let name: 'ariaE' | 'ariaH' = 'ariaE';
      if (setStateName.toLowerCase().includes('ariah')) name = 'ariaH';

      const amounts = formData.document[name]?.amounts;
      const newAmounts: typeof amounts = structuredClone(amounts);

      if (amounts && Array.isArray(setStateValue)) {
        for (const key of Object.keys(amounts) as (keyof typeof amounts)[]) {
          if (!setStateValue.includes(key)) {
            newAmounts![key] = null as unknown as number;
          }
        }

        formData.onChange?.({ [name]: { ...formData.document[name], amounts: newAmounts } });
      }
    };

  return (
    <React.Fragment>
      <FormRow title="general.date" headerWidth={platform === 'ms' ? 4 : 5}>
        <InputHandler
          type="PartialDate"
          editing={!disabledFields.includes('date')}
          name="date"
          formData={formData}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>

      {platform === 'ms' ? (
        <FormRow
          title={platform === 'ms' ? 'imaging.procedureCode' : 'imaging.code'}
          headerWidth={platform === 'ms' ? 4 : undefined}
        >
          <InputHandler
            type="TextField"
            editing={!disabledFields.includes('procedureCode')}
            name="procedureCode"
            formData={formData}
            placeholder={'imaging.procedureCodePlaceholder'}
          />
        </FormRow>
      ) : formData.document.procedureCode ? (
        <FormRow title="imaging.procedureCode">
          <InputHandler type="TextField" editing={false} name="procedureCode" formData={formData} />
        </FormRow>
      ) : null}

      {formData.document.statement && (
        <FormRow title="imaging.statement" headerWidth={platform === 'ms' ? 4 : 5}>
          <InputHandler type="TextArea" editing={false} name="statement" formData={formData} />
        </FormRow>
      )}

      <PlatformConditional platform="ms">
        <React.Fragment>
          <FormRow title="imaging.tesla" headerWidth={4}>
            <InputHandler
              type="RadioRow"
              editing={true}
              name="tesla"
              formData={formData}
              options={['0.5', '1.0', '1.5', '3.0']}
            />
          </FormRow>
          <FormRow title="imaging.wasGadoliniumGiven" headerWidth={4}>
            <InputHandler
              type="Radio"
              editing={true}
              name="wasGadoliniumGiven"
              formData={formData}
              options={['yes', 'no', 'unknown']}
              optionFormatter={optionFormatter('opts.yesNoUnknown')}
            />
          </FormRow>

          {previousMRIDoc && (
            <FormSection>
              <FormRow title="imaging.previousMRI" headerWidth={4}>
                <Container>
                  <Item>{previousMRIDoc.date ? formatPartialDate(previousMRIDoc.date) : '-'}</Item>
                  <Item xs={9} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ActionButtonRounded
                      text={previousDocument ? 'imaging.hidePrevData' : 'imaging.showPrevData'}
                      onClick={togglePrevious}
                      width={16}
                      height={3}
                      fontSize={16}
                      uppercase={false}
                    />
                  </Item>
                </Container>
              </FormRow>
            </FormSection>
          )}

          <FormSection header="imaging.brain">
            <StyledTable>
              <StyledTableBody>
                <LesionsFormTitleRow
                  title="imaging.brainT2Flair"
                  lesionsTitle="imaging.lesions"
                  deltaSymbol={<>&Delta;</>}
                  diameterSymbol="Ø"
                  trendTitle="imaging.trend"
                />
                <LesionsFormDataRow
                  formData={formData}
                  rowTitle="imaging.totalLesions"
                  lesions="brainT2Flair.total"
                  prevDoc={previousDocument}
                />
                <LesionsFormDataRow
                  formData={formData}
                  rowTitle="imaging.infratentorialLesions"
                  lesions="brainT2Flair.infratentorial"
                  prevDoc={previousDocument}
                />
                <LesionsFormDataRow
                  formData={formData}
                  rowTitle="imaging.supratentorialLesions"
                  lesions="brainT2Flair.supratentorial"
                  prevDoc={previousDocument}
                />
                <LesionsFormDataRow
                  formData={formData}
                  rowTitle="imaging.intraAndJuxtacorticalLesions"
                  lesions="brainT2Flair.intraAndJuxtacortical"
                  prevDoc={previousDocument}
                />
                <LesionsFormTitleRow title="imaging.brainT1" />
                <LesionsFormDataRow
                  formData={formData}
                  rowTitle="imaging.totalLesions"
                  lesions="brainT1.total"
                  prevDoc={previousDocument}
                />
                <LesionsFormDataRow
                  formData={formData}
                  rowTitle="imaging.gadoliniumEnhancedLesions"
                  lesions="brainT1.gadoliniumEnhanced"
                  prevDoc={previousDocument}
                />
                <StyledTableRow>
                  <StyledTableColumn style={{ verticalAlign: 'top' }}>
                    <StyledDiv>
                      <StyledRowTitle>{fm('imaging.brainVolume')}</StyledRowTitle>
                    </StyledDiv>
                  </StyledTableColumn>
                  <StyledTableColumn colSpan={4}>
                    <Container style={{ marginTop: '4rem' }}>
                      <Item>
                        <InputHandler
                          type="NumberField"
                          editing={true}
                          name="brainVolume"
                          formData={formData}
                          width={12}
                        />
                        {previousDocument && (
                          <StyledPreviousNumerical>{path(['brainVolume'], previousDocument)}</StyledPreviousNumerical>
                        )}
                      </Item>

                      <Item style={{ marginLeft: '3rem' }}>
                        <InputHandler
                          type="RadioTrendRow"
                          editing={true}
                          name="brainVolumeTrend"
                          formData={formData}
                          options={['decreased', 'same', 'increased']}
                          invert
                          previousValue={path(['brainVolumeTrend'], previousDocument)}
                        />
                      </Item>
                    </Container>
                  </StyledTableColumn>
                </StyledTableRow>
              </StyledTableBody>
            </StyledTable>
          </FormSection>
          <FormSection header="imaging.spinalCord">
            <StyledTable>
              <StyledTableBody>
                <LesionsFormTitleRow
                  title="imaging.spinalCordT2"
                  lesionsTitle="imaging.lesions"
                  deltaSymbol={<>&Delta;</>}
                  diameterSymbol="Ø"
                  trendTitle="imaging.trend"
                />
                <LesionsFormDataRow
                  formData={formData}
                  rowTitle="imaging.totalLesions"
                  lesions="spinalCordT2.total"
                  prevDoc={previousDocument}
                />
                <LesionsFormTitleRow title="imaging.spinalCordT1" />
                <LesionsFormDataRow
                  formData={formData}
                  rowTitle="imaging.gadoliniumEnhancedLesions"
                  lesions="spinalCordT1.gadoliniumEnhanced"
                  prevDoc={previousDocument}
                />
              </StyledTableBody>
            </StyledTable>
          </FormSection>
        </React.Fragment>
      </PlatformConditional>
      <PlatformConditional platform="parkinson">
        <React.Fragment>
          {formData.document.tesla ? (
            <FormRow title="imaging.tesla">
              <InputHandler
                type="RadioRow"
                editing={true}
                name="tesla"
                formData={formData}
                options={['1.5', '3.0', '5.0', '7.0']}
              />
            </FormRow>
          ) : null}
          <FormSection header={'imaging.whiteMatterChanges'}>
            <FormRow title="imaging.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous" id={'microAngiopathic'}>
              <InputHandler
                type="Radio"
                editing={true}
                name="wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous"
                formData={formData}
                options={['yes', 'fazekas0']}
                optionFormatter={optionFormatter('opts.yesFazekas0')}
              />
            </FormRow>
            {formData.document.wereMicroangiopathicChangesInWhiteMatterAbnormallyNumerous === 'yes' && (
              <React.Fragment>
                <FormRow title="imaging.wasFazekasClassificationDone" id={'fazekas'}>
                  <InputHandler
                    type="Radio"
                    editing={true}
                    name="wasFazekasClassificationDone"
                    formData={formData}
                    options={['yes', 'no']}
                    optionFormatter={optionFormatter('opts.yesNo')}
                  />
                </FormRow>
                {formData.document.wasFazekasClassificationDone === 'yes' && (
                  <FormRow title="imaging.fazekasGrade" id={'fazekasGrade'}>
                    <InputHandler
                      type="Radio"
                      editing={true}
                      name="fazekasGrade"
                      formData={formData}
                      options={['fazekas1', 'fazekas2', 'fazekas3']}
                      optionFormatter={optionFormatter('opts.fazekas')}
                    />
                  </FormRow>
                )}
              </React.Fragment>
            )}
            <FormRow title="imaging.atrophy">
              <InputHandler
                type="Radio"
                editing={true}
                name="atrophy"
                formData={formData}
                options={['no', 'appropriateForAge', 'pronouncedCommon', 'focal']}
                optionFormatter={optionFormatter('opts.atrophy')}
              />
            </FormRow>
            {formData.document.atrophy === 'focal' && (
              <FormRow title="imaging.atrophyLocalization">
                <InputHandler type="TextArea" editing={true} name="atrophyLocalization" formData={formData} />
              </FormRow>
            )}
          </FormSection>
          <FormSection header="imaging.otherClinicalRelevant">
            <FormRow title="imaging.otherClinicallyRelevantFindingFound" id={'otherRelevant'}>
              <InputHandler
                type="Radio"
                editing={true}
                name="otherClinicallyRelevantFindingFound"
                formData={formData}
                options={['yes', 'no']}
                optionFormatter={optionFormatter('opts.yesNo')}
              />
            </FormRow>
            {formData.document.otherClinicallyRelevantFindingFound === 'yes' && (
              <FormRow title="imaging.whatFinding">
                <InputHandler
                  type="TextArea"
                  editing={true}
                  name="otherClinicallyRelevantFinding"
                  formData={formData}
                />
              </FormRow>
            )}
          </FormSection>
        </React.Fragment>
        {isAlzheimerDiag ? (
          <React.Fragment>
            <div>
              {!ariaSectionsShown ? (
                <div>
                  <a
                    onClick={() => {
                      setAriaSectionsShown(true);
                    }}
                  >{`+ ${fm('imaging.opts.findingSections.aria')}`}</a>
                </div>
              ) : null}
            </div>
            <FormSection header={'imaging.ariaE.title'} condition={ariaSectionsShown}>
              <FormRow title="imaging.ariaE.finding">
                <InputHandler
                  type="Radio"
                  editing={true}
                  name="ariaE.finding"
                  formData={formData}
                  options={['no', 'mild', 'moderate', 'severe']}
                  optionFormatter={o => {
                    if (o === 'no') {
                      return fm(`imaging.opts.ariaE.${o}`);
                    }
                    return (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {fm(`imaging.opts.ariaE.${o}`)}
                        <ToolTip
                          content={
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginLeft: '1rem',
                              }}
                            >
                              <InfoIcon fontSize="small" sx={{ color: colors.info.default }} />
                            </div>
                          }
                          hover
                          description={fm(`imaging.opts.ariaE.${o}Desc`)}
                        />
                      </div>
                    );
                  }}
                  dependentFieldsList={val => {
                    if (val === 'no') {
                      setSelectedBrainAreasAriaE([]);
                      return alzBrainAreas.map(area => `ariaE.amounts.${area}`);
                    }
                    return [];
                  }}
                />
              </FormRow>
              {formData.document.ariaE?.finding && formData.document.ariaE?.finding !== 'no' ? (
                <React.Fragment>
                  <FormRow
                    title={'imaging.ariaE.amounts'}
                    description={fm('imaging.ariaE.amountsInfo')}
                    bottomMargin={false}
                  >
                    <InputHandler
                      name="brainAreasAriaE"
                      type="SelectAndAdd"
                      editing={true}
                      options={alzBrainAreas}
                      optionFormatter={o => fm(`imaging.brainAreas.${o}`)}
                      formData={{
                        document: { brainAreasAriaE: selectedBrainAreasAriaE },
                        onChange: handleChangeBrainAreas(setSelectedBrainAreasAriaE),
                      }}
                      placeholder={fm('imaging.brainAreasPlaceholder')}
                      listingStyle={'table'}
                      tableRowContent={o => [
                        <InputHandler
                          key={o}
                          type="NumberField"
                          editing={true}
                          name={`ariaE.amounts.${o}`}
                          formData={formData}
                          placeholder={fm('general.amount')}
                        />,
                      ]}
                    />
                  </FormRow>
                </React.Fragment>
              ) : undefined}
            </FormSection>
            <FormSection header="imaging.ariaH.title" condition={ariaSectionsShown} style={{ borderTop: 'none' }}>
              <FormRow title="imaging.ariaH.finding">
                <InputHandler
                  type="Radio"
                  editing={true}
                  name="ariaH.finding"
                  formData={formData}
                  options={['no', 'yes']}
                  optionFormatter={optionFormatter('opts.yesNo')}
                  dependentFieldsList={val => {
                    if (val === 'no') {
                      setSelectedBrainAreasAriaH([]);
                      return [...[...alzBrainAreas].map(area => `ariaH.amounts.${area}`), 'ariaH.ariaELeak'];
                    }
                    return [];
                  }}
                />
              </FormRow>
              {formData.document?.ariaH?.finding === 'yes' ? (
                <React.Fragment>
                  <FormRow title="imaging.ariaH.amounts">
                    <>
                      <InputHandler
                        name="brainAreasAriaH"
                        type="SelectAndAdd"
                        editing={true}
                        options={alzBrainAreas}
                        optionFormatter={o => fm(`imaging.brainAreas.${o}`)}
                        formData={{
                          document: { brainAreasAriaH: selectedBrainAreasAriaH },
                          onChange: handleChangeBrainAreas(setSelectedBrainAreasAriaH),
                        }}
                        placeholder={fm('imaging.brainAreasPlaceholder')}
                        listingStyle={'table'}
                        tableRowContent={o => [
                          <InputHandler
                            key={o}
                            type="NumberField"
                            editing={true}
                            name={`ariaH.amounts.${o}`}
                            formData={formData}
                            placeholder={fm('general.amount')}
                          />,
                        ]}
                      />
                      <div style={{ marginTop: '1rem', padding: 5, display: 'flex', width: '100%' }}>
                        <StyledSubHeader style={{ minWidth: '26rem' }}>
                          {fm('imaging.ariaH.totalAmount')}
                        </StyledSubHeader>
                        <InputHandler
                          type="NumberField"
                          editing={false}
                          name={'ariaHTotal'}
                          formData={{
                            document: {
                              ariaHTotal: Object.values(formData.document?.ariaH?.amounts ?? {}).reduce(
                                (prev, curr) => prev + curr,
                                0,
                              ),
                            },
                            onChange: () => null,
                          }}
                          placeholder={fm('general.amount')}
                        />
                      </div>
                    </>
                  </FormRow>
                </React.Fragment>
              ) : undefined}
              <FormRow
                title="imaging.ariaH.ariaELeak"
                condition={
                  !!(
                    formData.document?.ariaH?.finding === 'yes' &&
                    formData.document?.ariaE?.finding &&
                    formData.document.ariaE.finding !== 'no'
                  )
                }
              >
                <InputHandler
                  type="Radio"
                  editing={true}
                  name="ariaH.ariaELeak"
                  formData={formData}
                  options={['yes', 'no']}
                  optionFormatter={optionFormatter('opts.yesNo')}
                />
              </FormRow>
            </FormSection>
            <FormSection header="imaging.ariaH.ariaHOther" condition={ariaSectionsShown} style={{ borderTop: 'none' }}>
              <FormRow title="imaging.ariaH.ariaHOther">
                <InputHandler
                  type="Radio"
                  editing={true}
                  name="ariaH.ariaHOther"
                  formData={formData}
                  options={['no', 'yes']}
                  optionFormatter={optionFormatter('opts.yesNo')}
                  dependentFieldsList={() => ['ariaH.diameter']}
                />
              </FormRow>
              <FormRow title="imaging.ariaH.diameter" condition={formData.document?.ariaH?.ariaHOther === 'yes'}>
                <InputHandler
                  type="NumberField"
                  editing={true}
                  name="ariaH.diameter"
                  formData={formData}
                  precision={1}
                />
              </FormRow>
              <FormRow title="imaging.ariaNotes">
                <InputHandler type="TextArea" editing={true} name="ariaNotes" formData={formData} />
              </FormRow>
            </FormSection>
          </React.Fragment>
        ) : (
          <></>
        )}
      </PlatformConditional>
    </React.Fragment>
  );
};

interface IOwnProps {
  formData: IFormData<IMRI>;
  disabledFields?: string[];
}

interface ILesionsFormTitleRow {
  title: string;
  lesionsTitle?: string;
  deltaSymbol?: React.JSX.Element;
  diameterSymbol?: string;
  trendTitle?: string;
}

interface ILesionsFormDataRow extends IOwnProps {
  rowTitle: string;
  lesions: string | number;
  titlePadding?: string;
  prevDoc: IMRI | null;
}

export default MriForm;
